/**
 * SettingsPanel - Settings UI for adjusting game preferences
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSettingsStore } from '../../store/settingsStore';

interface SettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const SettingsPanel: React.FC<SettingsPanelProps> = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'audio' | 'display' | 'accessibility'>('audio');

  const {
    // Audio
    masterVolume,
    musicVolume,
    sfxVolume,
    voiceVolume,
    // Display
    textSpeed,
    showTypewriter,
    screenEffects,
    // Accessibility
    fontSize,
    highContrast,
    reducedMotion,
    // Actions
    setMasterVolume,
    setMusicVolume,
    setSFXVolume,
    setVoiceVolume,
    setTextSpeed,
    setShowTypewriter,
    setScreenEffects,
    setFontSize,
    setHighContrast,
    setReducedMotion,
    resetToDefaults,
  } = useSettingsStore();

  // Slider component
  const Slider: React.FC<{
    label: string;
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
  }> = ({ label, value, onChange, min = 0, max = 100, step = 1 }) => (
    <div className="mb-4">
      <div className="flex justify-between mb-2">
        <label className="text-sm font-medium text-white">{label}</label>
        <span className="text-sm text-white/70">{value}</span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
      />
    </div>
  );

  // Toggle component
  const Toggle: React.FC<{
    label: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    description?: string;
  }> = ({ label, checked, onChange, description }) => (
    <div className="mb-4">
      <div className="flex items-center justify-between">
        <div>
          <label className="text-sm font-medium text-white">{label}</label>
          {description && <p className="text-xs text-white/50 mt-1">{description}</p>}
        </div>
        <button
          onClick={() => onChange(!checked)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            checked ? 'bg-blue-600' : 'bg-gray-700'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              checked ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
    </div>
  );

  // Select component
  const Select: React.FC<{
    label: string;
    value: string;
    options: { value: string; label: string }[];
    onChange: (value: string) => void;
  }> = ({ label, value, options, onChange }) => (
    <div className="mb-4">
      <label className="text-sm font-medium text-white block mb-2">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );

  const tabs = [
    { id: 'audio' as const, label: 'Audio', icon: '🔊' },
    { id: 'display' as const, label: 'Display', icon: '🎨' },
    { id: 'accessibility' as const, label: 'Accessibility', icon: '♿' },
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/70 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />

          {/* Settings Panel */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-gray-900 rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] overflow-hidden pointer-events-auto"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="bg-gray-800 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-xl sm:text-2xl font-bold text-white">Settings</h2>
                <button
                  onClick={onClose}
                  className="text-white/70 hover:text-white transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Tabs */}
              <div className="bg-gray-800 px-2 sm:px-6 py-2 border-b border-gray-700 flex gap-1 sm:gap-2 overflow-x-auto">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'bg-blue-600 text-white'
                        : 'text-white/70 hover:text-white hover:bg-gray-700'
                    }`}
                  >
                    <span className="mr-1 sm:mr-2">{tab.icon}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-180px)] sm:max-h-[calc(80vh-180px)]">
                {/* Audio Tab */}
                {activeTab === 'audio' && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Audio Settings</h3>
                    <Slider label="Master Volume" value={masterVolume} onChange={setMasterVolume} />
                    <Slider label="Music Volume" value={musicVolume} onChange={setMusicVolume} />
                    <Slider label="Sound Effects" value={sfxVolume} onChange={setSFXVolume} />
                    <Slider label="Voice Volume" value={voiceVolume} onChange={setVoiceVolume} />
                  </div>
                )}

                {/* Display Tab */}
                {activeTab === 'display' && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Display Settings</h3>
                    <Slider
                      label="Text Speed"
                      value={textSpeed}
                      onChange={setTextSpeed}
                      min={0}
                      max={100}
                    />
                    <Toggle
                      label="Typewriter Effect"
                      checked={showTypewriter}
                      onChange={setShowTypewriter}
                      description="Show text letter-by-letter"
                    />
                    <Toggle
                      label="Screen Effects"
                      checked={screenEffects}
                      onChange={setScreenEffects}
                      description="Vignette, film grain, and scanlines"
                    />
                  </div>
                )}

                {/* Accessibility Tab */}
                {activeTab === 'accessibility' && (
                  <div>
                    <h3 className="text-base sm:text-lg font-semibold text-white mb-3 sm:mb-4">Accessibility Settings</h3>
                    <Select
                      label="Font Size"
                      value={fontSize}
                      options={[
                        { value: 'small', label: 'Small' },
                        { value: 'medium', label: 'Medium' },
                        { value: 'large', label: 'Large' },
                      ]}
                      onChange={(val) => setFontSize(val as 'small' | 'medium' | 'large')}
                    />
                    <Toggle
                      label="High Contrast"
                      checked={highContrast}
                      onChange={setHighContrast}
                      description="Increase contrast for better readability"
                    />
                    <Toggle
                      label="Reduced Motion"
                      checked={reducedMotion}
                      onChange={setReducedMotion}
                      description="Minimize animations and transitions"
                    />
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="bg-gray-800 px-3 sm:px-6 py-3 sm:py-4 border-t border-gray-700 flex justify-between gap-2">
                <button
                  onClick={resetToDefaults}
                  className="px-3 sm:px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white text-xs sm:text-sm font-medium transition-colors"
                >
                  Reset
                </button>
                <button
                  onClick={onClose}
                  className="px-4 sm:px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white text-xs sm:text-sm font-medium transition-colors"
                >
                  Done
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default SettingsPanel;
